import { session } from "./session";

export const API_BASE = ((import.meta.env.VITE_API_URL as string | undefined) ?? "http://localhost:3001").replace(/\/$/, "");

export class ApiError extends Error {
  constructor(public status: number, message: string, public data: Record<string, unknown> = {}) {
    super(message);
  }
  get code() {
    return (this.data.code as string | undefined) ?? null;
  }
}

/** Back-office calls carry the back-office token; a 401 for it signs out. */
async function send<T>(path: string, init: RequestInit): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("Accept", "application/json");
  const { token } = session.get();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(API_BASE + path, { ...init, headers, credentials: "omit" });
  const text = await res.text();
  let data: Record<string, unknown> = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { error: text };
  }
  if (!res.ok) {
    const err = new ApiError(res.status, (data.error as string) || `Request failed (${res.status})`, data);
    if (res.status === 401 && err.code === "office_auth") session.signOut();
    throw err;
  }
  return data as T;
}

export function api<T>(path: string, opts: { method?: string; body?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  return send<T>(path, {
    method: opts.method ?? (opts.body !== undefined ? "POST" : "GET"),
    headers: opts.body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });
}

/** A file upload (multipart) with the back-office token. */
export function upload<T>(path: string, form: FormData): Promise<T> {
  return send<T>(path, { method: "POST", body: form });
}

/** The shop's API lives under /api/v1/till; the back office uses its /admin routes. */
export const till = (p: string) => `/api/v1/till${p}`;
export const office = (p: string) => `/api/v1/office${p}`;

/** Store identity from the public storefront endpoint: name, logo, currency. */
export type Storefront = { storeName: string; packId: string | null; settings?: { logo?: string }; currency?: { code: string; symbol: string } };
export async function loadStorefront(): Promise<Storefront | null> {
  try {
    const res = await fetch(`${API_BASE}/api/v1/storefront`, { credentials: "omit" });
    return res.ok ? ((await res.json()) as Storefront) : null;
  } catch {
    return null;
  }
}
